import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

// Assets
import Trash01 from "@untitled-ui/icons-react/build/esm/Trash01";

// API related
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import directoryService from "#src/services/endpoint/directory";

// Components
import { Modal } from "@lockerpm/design";
import { ButtonV2 } from "#src/commonV2/Button";
import { createToast } from "#src/common/system/toasts";

type Props = {
  isOpen: boolean;
  workspaceId: string;
  departmentId: string;
  departmentName: string;
  closeModal: () => void;
  onSuccess: (id: string) => void;
};

const DeleteDepartmentModal = (props: Props) => {
  const {
    isOpen,
    workspaceId,
    departmentId,
    departmentName,
    onSuccess,
    closeModal,
  } = props;

  const { t } = useTranslation("endpoint", { keyPrefix: "directory" });

  // --------------- DATA ---------------

  const [isLoading, setIsLoading] = useState(false);

  // --------------- COMPUTED ---------------

  // --------------- METHODS ---------------

  // Delete deparment
  const deleteDepartment = useCallback(async () => {
    if (!workspaceId || !departmentId) {
      return;
    }

    setIsLoading(true);
    try {
      await directoryService.deleteDepartment(workspaceId, departmentId);
      createToast({
        type: "success",
        message: t("departments.notification.deleteDepartment.success.message"),
      });
      closeModal();
      onSuccess(departmentId);
    } catch (error) {
      apiErrorHandler(error);
    }

    setIsLoading(false);
  }, [workspaceId, departmentId, closeModal, onSuccess, t]);

  // --------------- EFFECTS ---------------

  // --------------- RENDER ---------------

  return (
    <Modal
      title={
        <div className="pl-14">
          <p className="relative z-10">{t("departments.delete_modal.title")}</p>
        </div>
      }
      open={isOpen}
      footer={null}
      maskClosable={false}
      onCancel={closeModal}
      className="v2 max-w-[600px] overflow-hidden relative"
      centered
      width="90%"
    >
      <div className="absolute -left-9 -top-11 z-0">
        <div className="border border-gray-v2-50 rounded-full p-5">
          <div className="border border-gray-v2-100 rounded-full p-5">
            <div className="border border-gray-v2-100 rounded-full p-5">
              <div className="rounded-full bg-error-v2-100 h-10 w-10 flex items-center justify-center border-4 border-error-v2-50">
                <Trash01 className="h-6 w-6 text-error-v2-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative v2 pl-14 z-10">
        {/* Desc */}
        <p className="text-md text-gray-v2-600 mb-3">
          {t("departments.delete_modal.desc1", { name: departmentName })}
        </p>
        <p className="text-md text-gray-v2-600">
          {t("departments.delete_modal.desc2")}
        </p>
        {/* Desc end */}

        {/* Footer */}
        <div className="flex gap-2 justify-end mt-8">
          <ButtonV2
            variant="secondary"
            size="md"
            color="gray"
            onClick={closeModal}
          >
            <p>{t("common.cancel")}</p>
          </ButtonV2>

          <ButtonV2
            variant="primary"
            size="md"
            color="error"
            pending={isLoading}
            onClick={deleteDepartment}
          >
            <p>{t("common.confirm")}</p>
          </ButtonV2>
        </div>
        {/* Footer end */}
      </div>
    </Modal>
  );
};

export default DeleteDepartmentModal;
